import clsx from 'clsx'
import { useState } from 'react'
import toast from 'react-hot-toast'

import { FvButton, OrDivider, TimeSelectField } from '@fv/client-components'
import type { ShipmentLocation } from '@fv/client-types'
import { fvDate } from '@fv/dates'
import { dates } from '@fv/models/core'

import {
  CarrierDateSelector,
  utcToCalendarDate,
} from '@/components/CarrierDateSelector'
import { usePutOppAccessorials } from '@/features/opp-details/mutations'

type StopAccessorialsProps = {
  accessorial?: ShipmentLocation['accessorials'][number]
  loadId: string
  location: ShipmentLocation
  pickupDate?: Date | string
  onClose: () => void
  className?: string
}

export const StopScheduleTime = ({
  accessorial,
  loadId,
  location,
  pickupDate,
  onClose,
  className,
}: StopAccessorialsProps) => {
  const [startTime, setStartTime] = useState(accessorial?.opensAt ?? '')
  const [endTime, setEndTime] = useState(accessorial?.closesAt ?? '')
  const [appointmentDate, setAppointmentDate] = useState(() =>
    utcToCalendarDate(accessorial?.appointmentDate ?? location.stopDate),
  )

  const putOppAccessorials = usePutOppAccessorials(loadId)
  const handleSave = async () => {
    if (startTime && endTime && startTime > endTime) {
      toast.error('End time must be after start time')
      return
    }
    await putOppAccessorials.mutateAsync({
      sequence: location.sequence,
      accessorial: {
        key: 'schedule',
        ...(appointmentDate && {
          appointmentDate: dates.utcNoon(
            fvDate(appointmentDate).format('YYYY-MM-DD'),
          ),
        }),
        ...(startTime && { opensAt: startTime }),
        ...(endTime && { closesAt: endTime }),
      },
    })
    onClose()
  }

  return (
    <div className={clsx('flex items-center gap-x-3', className)}>
      <CarrierDateSelector
        value={appointmentDate}
        pickupDate={pickupDate}
        tooltip="Appointment date"
        onChange={setAppointmentDate}
      />

      <TimeSelectField
        name="time"
        value={startTime}
        setValue={e => setStartTime(e)}
      />

      <span>- to -</span>

      <TimeSelectField
        name="time"
        value={endTime}
        setValue={e => setEndTime(e)}
      />
      <div className="flex items-center gap-x-2 ml-2">
        <FvButton
          theme="primary"
          onClick={handleSave}
          loading={putOppAccessorials.isPending}
          icon="check"
        >
          Save
        </FvButton>
        <OrDivider />
        <FvButton
          onClick={onClose}
          loading={putOppAccessorials.isPending}
          icon="times"
        >
          Cancel
        </FvButton>
      </div>
    </div>
  )
}
